import { z } from 'zod';
import {
  ATTENDANCE_CALC_MODES,
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_TYPES,
  ROLE_KEYS,
  WORK_ARRANGEMENTS,
} from './enums.js';
import { isCountryCode } from './countries.js';
import { isValidTimeZone } from './time.js';

/** Request-body schemas shared by the web app (form validation) and the API (enforcement). */

export const uuid = z.string().uuid('Please choose an option');
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'Invalid date');
export const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24h)');
export const timeZone = z
  .string()
  .refine(isValidTimeZone, 'Choose a valid IANA time zone (e.g. Asia/Beirut). Fixed offsets are not allowed.');
export const countryCode = z
  .string()
  .transform((s) => s.toUpperCase())
  .refine(isCountryCode, 'Choose a country');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .optional();
export const reason = z.string().trim().min(3, 'Please give a reason (min 3 characters)').max(500);

// ---------- Auth ----------
export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: z.string().min(8).max(256),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ---------- Departments ----------
export const departmentSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: optionalText(500),
    headEmployeeId: uuid.nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type DepartmentInput = z.infer<typeof departmentSchema>;

// ---------- Work schedules ----------
export const scheduleDaySchema = z
  .object({
    weekday: z.number().int().min(1).max(7),
    startTime: timeOfDay,
    endTime: timeOfDay,
    breakMinutes: z.number().int().min(0).max(600).default(0),
  })
  .strict()
  .refine((d) => d.startTime !== d.endTime, { message: 'Start and end time cannot be equal', path: ['endTime'] });

export const workScheduleSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: optionalText(500),
    lateGraceMinutes: z.number().int().min(0).max(240).default(0),
    earlyLeaveGraceMinutes: z.number().int().min(0).max(240).default(0),
    attendanceCalcMode: z.enum(ATTENDANCE_CALC_MODES).nullable().optional(),
    isActive: z.boolean().optional(),
    days: z
      .array(scheduleDaySchema)
      .max(7)
      .refine((days) => new Set(days.map((d) => d.weekday)).size === days.length, 'Each weekday may appear once'),
  })
  .strict();
export type WorkScheduleInput = z.infer<typeof workScheduleSchema>;

// ---------- Holidays ----------
export const holidayCalendarSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    countryCode: countryCode.nullable().optional(),
    description: optionalText(500),
    isActive: z.boolean().optional(),
    sourceType: z.enum(['manual', 'egypt_presidency']).optional(),
    sourceUrl: z.string().trim().url().max(500).nullable().optional(),
  })
  .strict();
export type HolidayCalendarInput = z.infer<typeof holidayCalendarSchema>;

export const holidaySchema = z
  .object({
    date: isoDate,
    name: z.string().trim().min(1).max(150),
    isHalfDay: z.boolean().default(false),
  })
  .strict();
export type HolidayInput = z.infer<typeof holidaySchema>;

export const holidaySyncSchema = z
  .object({
    /** Optional pasted page text, used when the server cannot reach the official site. */
    text: z.string().max(500_000).optional(),
  })
  .strict();

// ---------- Employees ----------
export const workTermsSchema = z
  .object({
    effectiveFrom: isoDate,
    timezone: timeZone,
    workScheduleId: z.string().uuid('Choose a work schedule'),
    holidayCalendarId: uuid.nullable().optional(),
    leavePolicyId: uuid.nullable().optional(),
  })
  .strict();
export type WorkTermsInput = z.infer<typeof workTermsSchema>;

const employeeProfileShape = {
  employeeCode: z
    .string()
    .trim()
    .min(1, 'Employee ID is required')
    .max(30, 'Max 30 characters')
    .regex(/^[A-Za-z0-9._-]+$/, 'Letters, digits, dot, dash or underscore only'),
  fullName: z.string().trim().min(2, 'Enter the full name').max(150),
  email: z.string().trim().toLowerCase().email('Enter a valid e-mail address').max(254),
  phone: optionalText(40),
  countryCode,
  city: optionalText(100),
  departmentId: uuid.nullable().optional(),
  position: optionalText(120),
  managerId: uuid.nullable().optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  employmentStatus: z.enum(EMPLOYMENT_STATUSES).default('active'),
  startDate: isoDate,
  endDate: isoDate.nullable().optional(),
  canApproveLeave: z.boolean().default(false),
  workArrangement: z.enum(WORK_ARRANGEMENTS).default('office'),
  officeName: optionalText(100),
  attendancePin: z
    .string()
    .trim()
    .max(32)
    .transform((s) => (s === '' ? null : s))
    .refine((s) => s === null || /^[A-Za-z0-9_-]+$/.test(s), 'Letters, digits, dash or underscore only')
    .nullable()
    .optional(),
  /** Used by the annual-leave rules (age 50+). Administrators only. */
  dateOfBirth: isoDate.nullable().optional(),
  /** Years of work experience before joining (any employer) — 10+ years in total gives 30 days. */
  priorExperienceYears: z.number().min(0).max(60).multipleOf(0.5, 'Use whole or half years').optional(),
  hasDisability: z.boolean().optional(),
};

export const employeeCreateSchema = z
  .object({
    ...employeeProfileShape,
    workTerms: workTermsSchema.omit({ effectiveFrom: true }).extend({ effectiveFrom: isoDate.optional() }),
    account: z
      .object({
        role: z.enum(ROLE_KEYS),
        /** If omitted a random temporary password is generated and returned once. */
        password: z.string().min(8).max(256).optional(),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict()
  .refine((e) => !e.endDate || e.endDate >= e.startDate, { message: 'End date must be after start date', path: ['endDate'] });
export type EmployeeCreateInput = z.infer<typeof employeeCreateSchema>;

export const employeeUpdateSchema = z
  .object({
    ...employeeProfileShape,
    employmentStatus: z.enum(EMPLOYMENT_STATUSES),
    canApproveLeave: z.boolean(),
    workArrangement: z.enum(WORK_ARRANGEMENTS),
    reason: z.string().trim().max(500).optional(),
  })
  .partial()
  .strict()
  .refine((e) => !e.endDate || !e.startDate || e.endDate >= e.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
  });
export type EmployeeUpdateInput = z.infer<typeof employeeUpdateSchema>;

export const newWorkTermsSchema = workTermsSchema.extend({ reason: reason.optional() }).strict();

export const deactivateEmployeeSchema = z
  .object({
    endDate: isoDate,
    reason,
  })
  .strict();

export const employeeSettingsSchema = z
  .object({
    attendanceCalcMode: z.enum(ATTENDANCE_CALC_MODES).nullable(),
    attendanceRequired: z.boolean(),
    hoursSource: z.enum(['attendance', 'computer']),
    trackWhen: z.enum(['clocked_in', 'schedule', 'always']).nullable(),
    payrollMode: z.enum(['standard', 'full', 'hourly']),
  })
  .partial()
  .strict();

// ---------- Accounts ----------
export const createAccountSchema = z
  .object({
    role: z.enum(ROLE_KEYS),
    password: z.string().min(8).max(256).optional(),
  })
  .strict();

export const updateAccountSchema = z
  .object({
    role: z.enum(ROLE_KEYS).optional(),
    isActive: z.boolean().optional(),
    /** Admins only: may see salaries and payroll. */
    payrollAccess: z.boolean().optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export const resetPasswordSchema = z
  .object({
    password: z.string().min(8).max(256).optional(),
  })
  .strict();

// ---------- Settings ----------
export const settingsPatchSchema = z
  .object({
    values: z.record(z.unknown()),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

// ---------- Listing ----------
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});

export const employeeListQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(100).optional(),
  departmentId: uuid.optional(),
  countryCode: z.string().length(2).toUpperCase().optional(),
  status: z.enum(EMPLOYMENT_STATUSES).optional(),
  managerId: uuid.optional(),
  workArrangement: z.enum(WORK_ARRANGEMENTS).optional(),
  includeInactive: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  sort: z.enum(['name', 'code', 'start_date', 'country']).default('name'),
});
